/** @format */

import { useCallback, useEffect, useState } from "react";
import {
  apiOrganigrammaRoles,
  apiOrganigrammaStructures,
  apiOrganigrammaTree,
  apiOrganigrammaUsers,
  organigrammaErrorMessage,
} from "../../../../api/apiOrganigramma";
import {
  TOrgPerson,
  TOrgRole,
  TOrgStructure,
  TOrgUser,
} from "../../../../redux-modules/declarations/maps/organigramma";

export interface TOrganigrammaData {
  tree: TOrgPerson[];
  users: TOrgUser[];
  roles: TOrgRole[];
  structures: TOrgStructure[];
  loading: boolean;
  /** true durante un aggiornamento successivo al primo caricamento */
  refreshing: boolean;
  error: string | null;
  /** ricarica tutto (dopo una modifica) */
  reload: () => Promise<void>;
  /** aggiornamenti ottimistici dopo una creazione inline */
  addRole: (role: TOrgRole) => void;
  addStructure: (structure: TOrgStructure) => void;
}

export const useOrganigrammaData = (): TOrganigrammaData => {
  const [tree, setTree] = useState<TOrgPerson[]>([]);
  const [users, setUsers] = useState<TOrgUser[]>([]);
  const [roles, setRoles] = useState<TOrgRole[]>([]);
  const [structures, setStructures] = useState<TOrgStructure[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (initial: boolean): Promise<void> => {
    if (initial) setLoading(true);
    else setRefreshing(true);

    try {
      const [treeData, usersData, rolesData, structuresData] = await Promise.all([
        apiOrganigrammaTree(),
        apiOrganigrammaUsers(),
        apiOrganigrammaRoles(),
        apiOrganigrammaStructures(),
      ]);
      setTree(treeData || []);
      setUsers(usersData || []);
      setRoles(rolesData || []);
      setStructures(structuresData || []);
      setError(null);
    } catch (err) {
      setError(organigrammaErrorMessage(err, "Impossibile caricare l'organigramma."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  const reload = useCallback(() => load(false), [load]);

  const addRole = useCallback((role: TOrgRole) => {
    setRoles(current =>
      [...current.filter(r => r.id !== role.id), role].sort((a, b) => a.name.localeCompare(b.name, "it")),
    );
  }, []);

  const addStructure = useCallback((structure: TOrgStructure) => {
    setStructures(current =>
      [...current.filter(s => s.id !== structure.id), structure].sort((a, b) =>
        a.name.localeCompare(b.name, "it"),
      ),
    );
  }, []);

  return {
    tree,
    users,
    roles,
    structures,
    loading,
    refreshing,
    error,
    reload,
    addRole,
    addStructure,
  };
};
